import { z } from 'zod';
import { normalizedUuidSchema, paginationMetaSchema, paginationSchema } from './common';
import { DESIGN_ASSET_TEMP_PREFIX } from './design-asset';
import {
  ALLOWED_IMAGE_CONTENT_TYPES,
  type AllowedImageContentType,
  IMAGE_CONTENT_TYPE_EXTENSIONS,
} from './image-asset';

/** 정책: docs/products/order-inquiry-policy.md */
export const ORDER_INQUIRY_KINDS = ['DEFECT', 'WRONG_ITEM', 'SHORTAGE', 'OTHER'] as const;
export type OrderInquiryKind = (typeof ORDER_INQUIRY_KINDS)[number];
export const orderInquiryKindSchema = z.enum(ORDER_INQUIRY_KINDS);
export const ORDER_INQUIRY_KIND_LABELS: Readonly<Record<OrderInquiryKind, string>> = {
  DEFECT: '불량',
  WRONG_ITEM: '오배송',
  SHORTAGE: '수량 부족',
  OTHER: '기타',
};

export const ORDER_INQUIRY_STATUSES = ['OPEN', 'ANSWERED'] as const;
export type OrderInquiryStatus = (typeof ORDER_INQUIRY_STATUSES)[number];
export const orderInquiryStatusSchema = z.enum(ORDER_INQUIRY_STATUSES);
export const ORDER_INQUIRY_STATUS_LABELS: Readonly<Record<OrderInquiryStatus, string>> = {
  OPEN: '접수됨',
  ANSWERED: '답변 완료',
};

export const ORDER_INQUIRY_RESOLUTIONS = [
  'REMAKE',
  'PARTIAL_REFUND',
  'FULL_REFUND',
  'REJECTED',
] as const;
export type OrderInquiryResolution = (typeof ORDER_INQUIRY_RESOLUTIONS)[number];
export const orderInquiryResolutionSchema = z.enum(ORDER_INQUIRY_RESOLUTIONS);
export const ORDER_INQUIRY_RESOLUTION_LABELS: Readonly<Record<OrderInquiryResolution, string>> = {
  REMAKE: '재제작',
  PARTIAL_REFUND: '부분 환불',
  FULL_REFUND: '전액 환불',
  REJECTED: '거절',
};

/** 접수 가능 상태(spec §4-4) — 화면이 버튼과 안내를 고른다. */
export const ORDER_INQUIRY_WRITABLE_STATES = [
  'WRITABLE',
  'NOT_STARTED',
  'ALREADY_OPEN',
  'NOT_ELIGIBLE',
] as const;
export type OrderInquiryWritableState = (typeof ORDER_INQUIRY_WRITABLE_STATES)[number];
export const orderInquiryWritableStateSchema = z.enum(ORDER_INQUIRY_WRITABLE_STATES);

export const ORDER_INQUIRY_ERROR_CODES = [
  'NOT_FOUND',
  'ORDER_NOT_ELIGIBLE',
  'NOT_STARTED',
  'ALREADY_OPEN',
  'INVALID_IMAGE',
  'CONFLICT',
] as const;
export type OrderInquiryErrorCode = (typeof ORDER_INQUIRY_ERROR_CODES)[number];

export const ORDER_INQUIRY_CONTENT_MIN = 10;
export const ORDER_INQUIRY_CONTENT_MAX = 1000;
export const ORDER_INQUIRY_ANSWER_MAX = 1000;
export const ORDER_INQUIRY_IMAGES_MAX = 5;
export const ORDER_INQUIRY_IMAGE_PRESIGNS_PER_HOUR = 30;
export const ORDER_INQUIRY_IMAGE_PREFIX = 'inquiries/';
/** 답변 완료 뒤 사진 보관 일수(정책 §5). */
export const ORDER_INQUIRY_IMAGE_RETENTION_DAYS = 365;

export const orderInquiryImageFileKeyFor = (input: {
  readonly imageId: string;
  readonly contentType: AllowedImageContentType;
}): string =>
  `${ORDER_INQUIRY_IMAGE_PREFIX}${input.imageId}.${IMAGE_CONTENT_TYPE_EXTENSIONS[input.contentType]}`;

/** 임시 경로는 후기·디자인 자산과 같은 tmp/uploads/ (버킷 수명주기 1일, ADR-0008). */
export const orderInquiryImageTemporaryKeyFor = (input: {
  readonly imageId: string;
  readonly contentType: AllowedImageContentType;
}): string =>
  `${DESIGN_ASSET_TEMP_PREFIX}${input.imageId}.${IMAGE_CONTENT_TYPE_EXTENSIONS[input.contentType]}`;

const timestampSchema = z.iso.datetime();
const codePointLength = (value: string): number => Array.from(value).length;

/** DB CHECK(char_length)와 같은 코드 포인트 단위. */
export const orderInquiryContentSchema = z
  .string()
  .trim()
  .refine(
    (value) => {
      const length = codePointLength(value);
      return length >= ORDER_INQUIRY_CONTENT_MIN && length <= ORDER_INQUIRY_CONTENT_MAX;
    },
    {
      message: `문의 내용은 ${ORDER_INQUIRY_CONTENT_MIN}자 이상 ${ORDER_INQUIRY_CONTENT_MAX}자 이하로 써 주세요.`,
    },
  );
export const orderInquiryAnswerSchema = z
  .string()
  .trim()
  .refine(
    (value) => codePointLength(value) >= 1 && codePointLength(value) <= ORDER_INQUIRY_ANSWER_MAX,
    {
      message: `답변은 ${ORDER_INQUIRY_ANSWER_MAX}자 이하로 써 주세요.`,
    },
  );
export const orderInquiryImageIdsSchema = z
  .array(normalizedUuidSchema)
  .max(ORDER_INQUIRY_IMAGES_MAX)
  .refine((ids) => new Set(ids).size === ids.length, {
    message: '같은 사진을 두 번 넣을 수 없습니다.',
  });

/** url 은 서명 URL. 보관 기간이 지나 객체를 지운 사진은 null(화면은 자리표시). */
export const orderInquiryImageViewSchema = z.strictObject({
  id: normalizedUuidSchema,
  url: z.string().nullable(),
  widthPx: z.number().int().positive(),
  heightPx: z.number().int().positive(),
});
export type OrderInquiryImageView = z.infer<typeof orderInquiryImageViewSchema>;

/** 고객이 보는 문의 한 건 — 운영자 id 는 싣지 않는다. */
export const orderInquirySchema = z.strictObject({
  id: normalizedUuidSchema,
  orderId: normalizedUuidSchema,
  kind: orderInquiryKindSchema,
  content: z.string(),
  images: z.array(orderInquiryImageViewSchema),
  status: orderInquiryStatusSchema,
  answer: z.string().nullable(),
  resolution: orderInquiryResolutionSchema.nullable(),
  answeredAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
});
export type OrderInquiry = z.infer<typeof orderInquirySchema>;

export const myOrderInquiriesInputSchema = z.strictObject({ orderId: normalizedUuidSchema });
export const myOrderInquiriesSchema = z.strictObject({
  inquiries: z.array(orderInquirySchema),
  writable: orderInquiryWritableStateSchema,
});
export type MyOrderInquiries = z.infer<typeof myOrderInquiriesSchema>;

export const createOrderInquiryInputSchema = z.strictObject({
  orderId: normalizedUuidSchema,
  kind: orderInquiryKindSchema,
  content: orderInquiryContentSchema,
  imageIds: orderInquiryImageIdsSchema,
});
export type CreateOrderInquiryInput = z.infer<typeof createOrderInquiryInputSchema>;

export const presignOrderInquiryImageInputSchema = z.strictObject({
  contentType: z.enum(ALLOWED_IMAGE_CONTENT_TYPES),
});
export const presignOrderInquiryImageResultSchema = z.strictObject({
  imageId: normalizedUuidSchema,
  uploadUrl: z.string(),
  expiresInSeconds: z.number().int().positive(),
});
export type PresignOrderInquiryImageResult = z.infer<typeof presignOrderInquiryImageResultSchema>;
export const confirmOrderInquiryImageInputSchema = z.strictObject({
  imageId: normalizedUuidSchema,
});

/** 관리자 목록 — 쪽 번호 방식(다른 관리자 목록과 같다). */
export const adminOrderInquiryListInputSchema = z.strictObject({
  ...paginationSchema.shape,
  status: orderInquiryStatusSchema.optional(),
  kind: orderInquiryKindSchema.optional(),
});
export type AdminOrderInquiryListInput = z.infer<typeof adminOrderInquiryListInputSchema>;

export const adminOrderInquiryRowSchema = z.strictObject({
  id: normalizedUuidSchema,
  orderId: normalizedUuidSchema,
  orderNumber: z.string(),
  /** 견적 담당자명(quote_requests.contact_name). 개인정보 조회 기록 없이 보여 준다(주문 목록과 같다, D15). */
  contactName: z.string(),
  kind: orderInquiryKindSchema,
  status: orderInquiryStatusSchema,
  resolution: orderInquiryResolutionSchema.nullable(),
  imageCount: z.number().int().nonnegative(),
  createdAt: timestampSchema,
  answeredAt: timestampSchema.nullable(),
});
export type AdminOrderInquiryRow = z.infer<typeof adminOrderInquiryRowSchema>;
export const adminOrderInquiryPageSchema = z.strictObject({
  items: z.array(adminOrderInquiryRowSchema),
  meta: paginationMetaSchema,
});
export type AdminOrderInquiryPage = z.infer<typeof adminOrderInquiryPageSchema>;
export const adminOrderInquiriesByOrderInputSchema = z.strictObject({
  orderId: normalizedUuidSchema,
});
export const adminOrderInquiriesByOrderSchema = z.strictObject({
  items: z.array(adminOrderInquiryRowSchema),
});

export const orderInquiryIdInputSchema = z.strictObject({ inquiryId: normalizedUuidSchema });

export const adminOrderInquiryDetailSchema = adminOrderInquiryRowSchema.extend({
  content: z.string(),
  images: z.array(orderInquiryImageViewSchema),
  answer: z.string().nullable(),
  answeredByName: z.string().nullable(),
  customer: z.strictObject({ name: z.string(), email: z.string(), withdrawn: z.boolean() }),
  updatedAt: timestampSchema,
});
export type AdminOrderInquiryDetail = z.infer<typeof adminOrderInquiryDetailSchema>;

export const answerOrderInquiryInputSchema = z.strictObject({
  inquiryId: normalizedUuidSchema,
  answer: orderInquiryAnswerSchema,
  resolution: orderInquiryResolutionSchema,
  expectedUpdatedAt: timestampSchema,
});
export type AnswerOrderInquiryInput = z.infer<typeof answerOrderInquiryInputSchema>;

export const orderInquiryMutationResultSchema = z.strictObject({
  id: normalizedUuidSchema,
  updatedAt: timestampSchema,
});
export type OrderInquiryMutationResult = z.infer<typeof orderInquiryMutationResultSchema>;
